import React from 'react';
import { Snackbar, Alert, Typography, Box } from '@mui/material';

const kindToSeverity = kind => {
  switch (kind) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    case 'success':
    default:
      return 'success';
  }
};

const ToastSubtitle = ({ title, subtitle }) => {
  if (subtitle == null || subtitle === '') return null;

  const messages = Array.isArray(subtitle)
    ? subtitle.map(item => String(item).trim()).filter(Boolean)
    : null;

  if (messages && messages.length > 1) {
    return (
      <Box
        component="ul"
        sx={{
          mt: title ? 0.5 : 0,
          mb: 0,
          pl: 2.25,
          lineHeight: 1.45,
          opacity: 0.92,
        }}>
        {messages.map((message, index) => (
          <Typography
            key={`${index}-${message.slice(0, 24)}`}
            component="li"
            variant="body2"
            sx={{ display: 'list-item' }}>
            {message}
          </Typography>
        ))}
      </Box>
    );
  }

  const text =
    messages && messages.length === 1
      ? messages[0]
      : typeof subtitle === 'string'
      ? subtitle
      : String(subtitle);

  return (
    <Typography
      component="div"
      variant="body2"
      sx={{ mt: title ? 0.5 : 0, lineHeight: 1.4, opacity: 0.92 }}>
      {text}
    </Typography>
  );
};

/**
 * Global toast — MUI Snackbar + Alert.
 * Filled colors come from the app theme: `toastFilledAlert` + `MuiAlert` overrides in theme.js.
 * `subtitle` may be a string or a string[] (rendered as a bullet list when multiple).
 */
const ToastNotifications = ({ open, kind, title, subtitle, onClose }) => {
  const severity = kindToSeverity(kind);
  const multiError =
    kind === 'error' && Array.isArray(subtitle) && subtitle.length > 1;
  const autoHideDuration =
    kind === 'success' ? 3000 : multiError ? 10000 : 6000;

  const handleSnackbarClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    onClose();
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleSnackbarClose}
      disablePortal
      sx={{
        position: 'static',
        height: 'auto',
        width: '100%',
        maxWidth: 560,
        transform: 'none',
        left: 'auto',
        right: 'auto',
        top: 'auto',
        bottom: 'auto',
      }}>
      <Alert
        severity={severity}
        variant="filled"
        sx={{
          width: '100%',
          alignItems: 'flex-start',
          boxShadow: 3,
        }}>
        <Box
          sx={{
            pr: 0.5,
            color: 'inherit',
            '& .MuiTypography-root': { color: 'inherit' },
          }}>
          {title ? (
            <Typography
              component="div"
              variant="subtitle2"
              sx={{ fontWeight: 600, lineHeight: 1.35 }}>
              {title}
            </Typography>
          ) : null}
          <ToastSubtitle title={title} subtitle={subtitle} />
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default ToastNotifications;
